// src/context/UserContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { getConfig, applyJourneyOrgToAuthorizationParams } from '../config';

const UserContext = createContext();

const onRedirectCallback = (appState) => {
  // No custom history in React Router v6 from here.
  // Just replace the URL; your <BrowserRouter> will handle the route.
  const targetUrl = appState?.returnTo || window.location.pathname;
  window.history.replaceState({}, document.title, targetUrl);
};

export const UserProvider = ({ children }) => {
  const [selectedLocale, setSelectedLocale] = useState('en-US');
  const { journeyConfig = {} } = getConfig();
  const defaultJourneyConfig = journeyConfig.email1 || {};
  const resolveJourneyConfig = (journey) =>
    journeyConfig[journey] || defaultJourneyConfig;

  // Invitation links must not force a default app — user selects a journey.
  // Optional ?journey= still applies; otherwise leave empty so the UI requires a choice.
  const [selectedJourney, setSelectedJourney] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const journeyParam = params.get('journey');
    if (params.has('invitation')) {
      return journeyParam || '';
    }
    return journeyParam || localStorage.getItem('selectedJourney') || 'email1';
  });

  const [currentConfig, setCurrentConfig] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const journeyParam = params.get('journey');
    const journey = params.has('invitation')
      ? (journeyParam || '')
      : (journeyParam || localStorage.getItem('selectedJourney') || 'email1');
    return resolveJourneyConfig(journey);
  });

  useEffect(() => {
    localStorage.setItem('selectedJourney', selectedJourney);
  }, [selectedJourney]);

  const getClientConfig = () => resolveJourneyConfig(selectedJourney);

  const updateJourney = (journey) => {
    setSelectedJourney(journey);
    setCurrentConfig(resolveJourneyConfig(journey));
    localStorage.setItem('selectedJourney', journey);
  };

  const getAuthParams = () => {
    const baseParams = {
      redirect_uri: window.location.origin,
      // Add audience/scope here if needed:
      // audience: currentConfig.audience,
      // scope: 'openid profile email'
    };

    // Extract all params from URL if invitation is present
    const params = new URLSearchParams(window.location.search);
    if (params.has('invitation')) {
      const authParams = { ...baseParams };
      
      // Pass through all URL params except error-related and journey params
      params.forEach((value, key) => {
        if (key !== 'error' && key !== 'error_description' && key !== 'journey') {
          authParams[key] = value;
        }
      });

      return applyJourneyOrgToAuthorizationParams(authParams, currentConfig);
    }

    if (selectedJourney === 'mfa_mandatory') {
      var mfaParams = {
        ...baseParams,
        acr_values: 'urn:okta:loa:2fa:any',
        prompt: 'login',
      };
      if (currentConfig.authorizationParams) {
        mfaParams = { ...mfaParams, ...currentConfig.authorizationParams };
      }
      return applyJourneyOrgToAuthorizationParams(mfaParams, currentConfig);
    }

    var merged = { ...baseParams };
    if (currentConfig.authorizationParams) {
      merged = { ...merged, ...currentConfig.authorizationParams };
    }
    return applyJourneyOrgToAuthorizationParams(merged, currentConfig);
  };

  // Force remount of Auth0Provider when journey changes
  const providerKey = `${selectedJourney}-${currentConfig.clientId}`;

  return (
    <UserContext.Provider
      value={{
        selectedLocale,
        setSelectedLocale,
        selectedJourney,
        setSelectedJourney: updateJourney,
        getClientConfig,
      }}
    >
      <Auth0Provider
        key={providerKey}
        domain={currentConfig.domain}
        clientId={currentConfig.clientId}
        authorizationParams={getAuthParams()}
        onRedirectCallback={onRedirectCallback}
        useRefreshTokens={true}
        cacheLocation="localstorage"
      >
        {children}
      </Auth0Provider>
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
